import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import { captureException } from '@/errors';
import { initStore, pollStore } from '@/stores/store-utils';
import { PopulationInfo } from '@/types/overlaps';
import { Population } from '@/types/populations';
import {
  FilterableSource,
  Source,
  SourceField,
  SourceMdm,
} from '@/types/sources';
import urls from '@/urls';

export const useSourcesStore = defineStore('SourcesStore', () => {
  const popSourceIdToSourcesMap = ref<Record<number, Source[]>>({});
  const sources = ref<Source[]>([]);

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      const { data } = await crossbeamApi.GET('/v0.1/sources', {
        params: {
          query: {
            include_deleted: true,
          },
        },
      });

      // TODO: fix casting once we add explicit data_type to SourceFields in different endpoints
      sources.value = (data?.items as Source[]) ?? [];
    },
  );

  refresh({ useCache: true });

  const dealSources = computed(() => {
    return sources.value?.filter(({ mdm_type: type }) => type === 'deal') ?? [];
  });

  const contactSources = computed(() => {
    return (
      sources.value?.filter(({ mdm_type: type }) => type === 'contact') ?? []
    );
  });

  const productSources = computed(() => {
    return (
      sources.value?.filter(({ mdm_type: type }) => type === 'product') ?? []
    );
  });

  /**
   * Whether the feed syncs product data: both its product source and its deal
   * product (line item) source. Deals only link to products through line
   * items, and product search (get-product-types in muncher) only returns
   * products used on a line item. Sources can exist without syncing (Products not
   * selected at feed setup). A missing sync_enabled counts as syncing, like
   * the data source setup payload does.
   */
  function hasSyncedProductData(feedId: number | null | undefined) {
    if (feedId === null || feedId === undefined) return false;
    function syncsOnFeed(mdmType: SourceMdm) {
      return sources.value.some(
        (source) =>
          source.mdm_type === mdmType &&
          source.feed_id === feedId &&
          (source.sync_enabled ?? true),
      );
    }
    return syncsOnFeed('product') && syncsOnFeed('deal_product');
  }

  function getSourceById(id: number) {
    return sources.value.find((source) => source.id === id);
  }

  function getSourcesByFeedId(feedId: number) {
    return sources.value.filter((source) => source.feed_id === feedId);
  }

  function getAllFilterableSources(
    items: FilterableSource[],
  ): FilterableSource[] {
    return items.map((source) => {
      const { id, fields, table, mdm_type, is_base_table, schema } = source;
      source['filterable-sources'].unshift({
        fields,
        id,
        is_base_table,
        mdm_type,
        schema,
        table,
      });
      return source;
    });
  }

  function getSourceFieldById(sourceFieldId: number): SourceField | undefined {
    const source = sources.value.find((source) =>
      source.fields.find((sourceField) => sourceField.id === sourceFieldId),
    );
    const field = source?.fields.find(
      (sourceField) => sourceField.id === sourceFieldId,
    );
    return field ? { ...field } : undefined;
  }

  function getSourcesByMdmType(parentMdmType: SourceMdm) {
    return (
      sources.value?.filter(({ mdm_type: type }) => type === parentMdmType) ??
      []
    );
  }

  function getSourceByFeedIdAndMdmType(feedId: number, mdmType: SourceMdm) {
    return getSourcesByFeedId(feedId).find(
      (source) => source.mdm_type === mdmType,
    );
  }

  function getRelatedSources(source: Source) {
    const datasetName = source.dataset_name;
    return datasetName
      ? sources.value.filter((source) => source.dataset_name === datasetName)
      : [source];
  }

  function resetSourcesByPopulationSourceId() {
    popSourceIdToSourcesMap.value = {};
  }

  async function setSourcesByPopulationSourceId(
    population: Population | PopulationInfo,
  ) {
    const { source_id: sourceId } = population;
    if (popSourceIdToSourcesMap.value[sourceId]) return;
    try {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/sources/{id}/filterable-sources',
        {
          params: { path: { id: sourceId } },
        },
      );
      if (error) throw error;
      popSourceIdToSourcesMap.value[sourceId] =
        data.items as FilterableSource[]; // TODO: fix casting once we add explicit data_type to SourceFields in different endpoints
    } catch (xhr) {
      captureException(xhr);
    }
  }

  function upsertSource(source: Source) {
    const index = sources.value.findIndex((s) => s.id === source.id);
    if (index === -1) {
      sources.value.push(source);
      return;
    }
    sources.value[index] = source;
  }

  async function deleteSource(id: number) {
    try {
      const url = urls.sources.delete(id);
      await axios.delete(url);
    } catch (xhr) {
      captureException(xhr);
    }
  }

  const { isPolling, startPolling, stopPolling } = pollStore(
    readySync,
    refresh,
  );

  return {
    error: initError,
    ready,
    readySync,
    running,
    sources,
    popSourceIdToSourcesMap,
    refreshSourcesStore: refresh,
    dealSources,
    contactSources,
    productSources,
    hasSyncedProductData,
    getSourceById,
    getSourcesByFeedId,
    getAllFilterableSources,
    getSourceFieldById,
    getSourcesByMdmType,
    getSourceByFeedIdAndMdmType,
    getRelatedSources,
    resetSourcesByPopulationSourceId,
    setSourcesByPopulationSourceId,
    upsertSource,
    deleteSource,
    isPolling,
    startPolling,
    stopPolling,
  };
});
